/**
 * The PQL replacement screens (PRD Amendment 6, A96–A102): visit history
 * pulls, saved store and coordinate lists, and the iOOH zip lookup. Shared by the routes, the stubs and the screens.
 */
import type { PlaceAttributes } from '../ooh/place-attributes';
import { z } from 'zod';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

export const VISIT_CATEGORIES = [
    'Quick-service restaurants',
    'Grocery',
    'Big-box retail',
    'Pharmacy',
    'Convenience and fuel',
    'Home improvement',
    'Wireless carrier stores',
    'Coffee shops',
    'Department stores',
    'Auto dealers',
] as const;

/** Days of the week, Monday first (Snowflake's DAYOFWEEKISO order). */
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

/** A pull that reads the pings at the places: anyone who passed by, alone or with the visits. */
export function readsPings(presence: string | undefined): boolean {
    return presence === 'passed' || presence === 'either';
}

/**
 * Panel profile answers a pull can narrow by (M4S-5904), each a column of the
 * warehouse's panelist table, matched as the panel labels them.
 */
export const PULL_ATTRIBUTES = [
    { key: 'ethnicity', label: 'Ethnicity', column: 'ETHNICITY' },
    { key: 'income', label: 'Household income', column: 'INCOME' },
    { key: 'education', label: 'Education', column: 'EDUCATION' },
    { key: 'employment', label: 'Employment', column: 'EMPLOYMENT_STATUS' },
    { key: 'marital', label: 'Marital status', column: 'MARITAL_STATUS' },
    { key: 'children', label: 'Children in the home', column: 'GENDER_AGE_CHILD' },
    { key: 'division', label: 'Census division', column: 'US_CENSUS_DIVISION' },
] as const;
export type PullAttributeKey = (typeof PULL_ATTRIBUTES)[number]['key'];
const ATTRIBUTE_KEYS = PULL_ATTRIBUTES.map((a) => a.key) as [
    PullAttributeKey,
    ...PullAttributeKey[],
];

/** The pull names places: chains, categories or a file of places. */
export function hasPlaces(v: {
    chains?: unknown[];
    categories?: unknown[];
    listId?: string;
}): boolean {
    return Boolean(v.chains?.length || v.categories?.length || v.listId);
}

/**
 * The pull says who, beyond the window (M4S-5904): what a pull with no places
 * needs, so it never reads the whole panel.
 */
export function saysWho(v: {
    age?: unknown;
    genders?: unknown[];
    states?: unknown[];
    dmas?: unknown[];
    regions?: unknown[];
    attributes?: Record<string, unknown>;
    livesIn?: unknown;
    people?: unknown;
    device?: unknown;
    switched?: unknown;
    did?: unknown;
    participation?: unknown;
}): boolean {
    return Boolean(
        v.age ||
        v.genders?.length ||
        v.states?.length ||
        v.dmas?.length ||
        v.regions?.length ||
        Object.keys(v.attributes ?? {}).length ||
        v.livesIn ||
        v.people ||
        v.device ||
        v.switched ||
        v.did ||
        v.participation,
    );
}

/**
 * "Who visited this chain, category or list of places in this date range"
 * (A96); or, with no places, who the panel holds by profile, behaviour, phone
 * or a file of IDs (M4S-5904).
 */
export const VisitPullInputSchema = z
    .object({
        chains: z
            .array(z.object({ chainId: z.string().min(1), name: z.string().min(1) }))
            .max(20)
            .default([]),
        /** "any": visited at least one of the chains; "all": visited every one of them. */
        chainMatch: z.enum(['any', 'all']).default('any'),
        categories: z.array(z.enum(VISIT_CATEGORIES)).max(10).default([]),
        listId: z.string().optional(),
        /** A network's screens, one market at a time (`marketOf`, files/screen-actions.ts). */
        listMarket: z.string().trim().min(1).max(80).optional(),
        from: isoDate,
        to: isoDate,
        /**
         * Who counts at the places (M4S-5904); left out, "visit". "visit": a visit the visit data
         * detected, at least `dwellMinutes` long. "passed": anyone whose phone
         * pinged inside a saved list's radius, drive-bys included: the wider
         * audience for out-of-home placements. "either": both, each person once.
         */
        presence: z.enum(['visit', 'passed', 'either']).optional(),
        /**
         * When, in each place's local time (M4S-5904): hours from (inclusive) to
         * (exclusive), wrapping past midnight when from is later than to, and the
         * days of the week.
         */
        hours: z
            .object({ from: z.number().int().min(0).max(23), to: z.number().int().min(1).max(24) })
            .refine((h) => h.from !== h.to, {
                message: 'The hours start and end at the same time.',
            })
            .optional(),
        weekdays: z.array(z.enum(WEEKDAYS)).min(1).max(7).optional(),
        /** How often: seen on at least this many days, and at this many different places. */
        minDays: z.number().int().min(2).max(366).optional(),
        minPlaces: z.number().int().min(2).max(5000).optional(),
        /** A path: then seen at these places (a saved file, or chains) within this many days. */
        then: z
            .object({
                listId: z.string().optional(),
                chains: z
                    .array(z.object({ chainId: z.string().min(1), name: z.string().min(1) }))
                    .max(20)
                    .optional(),
                withinDays: z.number().int().min(1).max(90),
            })
            .refine((t) => t.listId || t.chains?.length, {
                message: 'Pick where they went next: a file of places or a chain.',
            })
            .optional(),
        /** "passed" and "either": one radius for every place, in place of the file's own. */
        radiusMeters: z.number().int().min(10).max(1000).optional(),
        dwellMinutes: z.number().int().min(1).max(240).default(5),
        confidence: z.enum(['any', 'medium', 'high']).default('medium'),
        usOnly: z.boolean().default(true),
        age: z.object({ min: z.number().int(), max: z.number().int() }).optional(),
        genders: z.array(z.enum(['male', 'female', 'non_binary'])).optional(),
        states: z.array(z.string().length(2)).optional(),
        /** DMA names and census regions, as the panel records them (M4S-5904). */
        dmas: z.array(z.string().min(1).max(80)).max(210).optional(),
        regions: z.array(z.string().min(1).max(40)).max(4).optional(),
        /** Profile answers, each matched as the panel labels them. */
        attributes: z
            .record(z.enum(ATTRIBUTE_KEYS), z.array(z.string().min(1).max(120)).min(1).max(40))
            .optional(),
        /** Where they live: these zip codes, or within this many miles of them. */
        livesIn: z
            .object({
                zips: z
                    .array(z.string().regex(/^\d{5}$/, 'Zip codes are five digits.'))
                    .min(1)
                    .max(5000),
                radiusMiles: z.number().min(0).max(100),
            })
            .optional(),
        /** People from a file of panelist IDs: only them, or everyone but them. */
        people: z
            .object({
                name: z.string().trim().min(1).max(120),
                ids: z
                    .array(z.string().regex(/^\d+$/, 'Panelist IDs are numbers.'))
                    .min(1)
                    .max(200_000),
                exclude: z.boolean(),
            })
            .optional(),
        /** Their phone now: brand, platform and carrier, any of each. */
        device: z
            .object({
                makes: z.array(z.string().min(1).max(60)).max(20).optional(),
                platforms: z
                    .array(z.enum(['ios', 'android']))
                    .max(2)
                    .optional(),
                carriers: z.array(z.string().min(1).max(60)).max(20).optional(),
            })
            .optional(),
        /** People who switched phone brand or carrier during the window. */
        switched: z
            .object({
                switchType: z.enum(['device_brand', 'carrier']),
                from: z.array(z.string().min(1).max(80)).max(20).optional(),
                to: z.array(z.string().min(1).max(80)).min(1).max(20),
            })
            .optional(),
        /**
         * What they did in the window (M4S-5904): used any of these apps or
         * visited any of these websites (either), and bought at any of these
         * stores, spending at least minSpend there in all. Names exactly as the
         * meter and receipts record them.
         */
        did: z
            .object({
                apps: z.array(z.string().min(1).max(200)).max(20).optional(),
                sites: z.array(z.string().min(1).max(200)).max(20).optional(),
                stores: z.array(z.string().min(1).max(160)).max(20).optional(),
                minSpend: z.number().positive().max(100_000).optional(),
            })
            .refine((d) => d.apps?.length || d.sites?.length || d.stores?.length, {
                message: 'Pick an app, a website or a store.',
            })
            .optional(),
        /** Include or exclude by past survey participation (A102). */
        participation: z
            .object({
                mode: z.enum(['include', 'exclude']),
                surveyIds: z.array(z.number().int().min(1)).min(1),
                /**
                 * Which outcomes count as taking part (M4S-5904, PQL's survey
                 * attributes): completed, screened out (terminated), dropped out
                 * (retired), or sent by a location (waypoint). Left out: the
                 * first three.
                 */
                outcomes: z
                    .array(z.enum(['completed', 'terminated', 'retired', 'waypoint']))
                    .min(1)
                    .max(4)
                    .optional(),
            })
            .optional(),
    })
    .refine((v) => hasPlaces(v) || saysWho(v), {
        message:
            'Pick places, or say who they are: a profile answer, what they did, their phone or a file of people.',
    })
    .refine(
        (v) =>
            hasPlaces(v) ||
            !(
                v.hours ||
                v.weekdays ||
                v.minDays ||
                v.minPlaces ||
                v.then ||
                readsPings(v.presence)
            ),
        { message: 'Time of day, how often and where they went next need places.' },
    )
    .refine((v) => v.from <= v.to, { message: 'The window starts after it ends.' })
    .refine((v) => v.chainMatch === 'any' || v.chains.length >= 2, {
        message: 'Pick at least two chains to require all of them.',
    })
    .refine((v) => v.chainMatch === 'any' || (!v.categories.length && !v.listId), {
        message: 'All of these works with chains only. Clear the categories and saved list.',
    })
    .refine(
        (v) => !readsPings(v.presence) || (v.listId && !v.chains.length && !v.categories.length),
        {
            message:
                'Anyone who passed by works with a file of places only. Clear the chains and categories.',
        },
    );
export type VisitPullInput = z.infer<typeof VisitPullInputSchema>;
export type VisitPresence = NonNullable<VisitPullInput['presence']>;
/** What the screen sends: defaults may be left out. */
export type VisitPullRequest = z.input<typeof VisitPullInputSchema>;

export interface VisitPullResponse {
    pullId: string;
    count: number;
    byPlace: { name: string; visitors: number }[];
    byWeek: { weekOf: string; visitors: number }[];
    /** The panelist IDs, for the CSV and the release group (capped for the screen). */
    panelistIds: string[];
    idsTruncated: boolean;
    eligibilityRulesVersion: number;
    description: string;
    /** Who counted at the places (M4S-5904); "anyone" for a pull with no places. */
    presence: NonNullable<VisitPullInput['presence']> | 'anyone';
    /** More people than the files keep: how many IDs were kept (the first, by ID). */
    idsKept?: number;
    /** "passed" only: how many of them stopped (two or more pings at one place on one day). */
    stopped?: number;
    /** "passed" only: places in the file that caught no one (worth checking their coordinates). */
    placesWithNoOne?: number;
}

/**
 * A visit pull DANI asked to run in the chat (`feasibility-visits` block,
 * M4S-5904). The list is a saved list, named by the id Studio gave DANI when
 * the person attached it, or by its name. Who counts defaults to anyone who
 * passed by, the wider out-of-home audience.
 */
export const VisitsBlockSchema = z
    .object({
        listId: z.string().min(1).optional(),
        listName: z.string().min(1).max(120).optional(),
        presence: z.enum(['visit', 'passed', 'either']).default('passed'),
        from: isoDate,
        to: isoDate,
        radiusMeters: z.number().int().min(10).max(1000).optional(),
        dwellMinutes: z.number().int().min(1).max(240).optional(),
    })
    .refine((v) => v.listId || v.listName, { message: 'Name the file.' })
    .refine((v) => v.from <= v.to, { message: 'The window starts after it ends.' });
export type VisitsBlock = z.infer<typeof VisitsBlockSchema>;

export const ReleaseGroupInputSchema = z.object({
    pullId: z.string().min(1),
    name: z.string().trim().min(3).max(120),
    /** Why the groups go over the rate card's size (UAT UI-019); without one, the send is split. */
    overrideReason: z.string().trim().max(300).optional(),
});

export interface ReleaseGroupResponse {
    releaseGroupId: string;
    name: string;
    size: number;
    handedOffTo: string;
    /** Every group made: a send over the size is split (UAT UI-019). */
    groups: { id: string; name: string }[];
    largestGroup: number;
    maxGroupSize: number;
}

/**
 * Every kind of saved file (docs/pdlc/feasibility-files.md §4). Stores and
 * placements are the A97 kinds; the rest came with Files. A brief is not a
 * list (it is kept in pdlc_feasibility_brief) and an ad log belongs to a
 * campaign list (pdlc_feasibility_ad_log).
 */
export const LIST_KINDS = [
    'stores',
    'placements',
    'zips',
    'panelists',
    'screens',
    'campaign',
] as const;
export type ListKind = (typeof LIST_KINDS)[number];

export type SavedPlacement = { lat: number; lng: number; radiusMeters: number; type?: string };

/** A screen of an ad network (Ad Network Intelligence), beside its placement at the same index. */
export interface SavedScreen {
    screenId: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    format?: string;
    venue?: string;
    /**
     * Every other column of the network's file, under its header in snake_case
     * (Bulletin's business_type, Outfront's media_format): DANI's location file
     * keeps them, and the ANI report cuts by them.
     */
    attributes?: Record<string, string | number>;
}

/** A campaign placement, beside its point at the same index: the ad logs name it by TabID. */
export interface SavedCampaignPlacement {
    tabId: string;
    market?: string;
    start?: string;
    end?: string;
}

/** What a kind keeps beyond chains and placements (the list's `content`). */
export interface SavedListContent {
    /** Placements: each placement's group, same order as placements; absent when the file had none. */
    roles?: ('test' | 'control' | null)[];
    zips?: string[];
    zipMarkets?: { name: string; zips: string[] }[];
    panelistIds?: string[];
    screens?: SavedScreen[];
    campaign?: SavedCampaignPlacement[];
    /**
     * Placements: each place's own columns from the file (venue ID, name, type,
     * address), for the delivery file (M4S-5927). Server only: list responses
     * leave it out (`withoutAttributes`).
     */
    attributes?: PlaceAttributes;
}

export interface CampaignInfo {
    name: string;
    flightStart?: string;
    flightEnd?: string;
}

/** A list uploaded once, matched, saved, and re-run later without reformatting (A97). */
export interface SavedList {
    id: string;
    kind: ListKind;
    name: string;
    rows: number;
    /** Stores: matched to chains and places. Placements: rows read. */
    matched: number;
    unmatched: number;
    /** A list with coordinates (UAT UI-008): each chain's matched locations in the fielding index. */
    chains?: { chainId: string; name: string; locations: number; locationIds?: string[] }[];
    placements?: SavedPlacement[];
    content?: SavedListContent;
    /** The client it is for: a Salesforce account id and its name (screens, campaigns). */
    orgId?: string;
    orgName?: string;
    /** Screens: the ad network. */
    network?: string;
    campaign?: CampaignInfo;
    fileName?: string;
    /** Rows refused when it was read, by row ("Row 12: no screen id."), at most 200. */
    refusals?: string[];
    version?: number;
    replacesId?: string;
    mode?: 'replace' | 'add';
    publishedAt?: string | null;
    /** Why the last publish to DANI failed; cleared by the next one that succeeds. */
    publishError?: string;
    createdBy: string;
    createdAt: string;
    lastUsedAt: string | null;
    /** Only when the list is saved: store names no chain matched, with their store counts. */
    unmatchedNames?: string[];
    /**
     * Set when the list came without its contents (the Files page and pickers,
     * `GET /lists?summary=1`): its counts and screen markets. The contents load
     * by id when an action needs them (BT-2223, 2026-10-30).
     */
    summary?: ListSummary;
}

/** What a list holds, counted, for a list sent without its contents. */
export interface ListSummary {
    places: number;
    test: number;
    control: number;
    zips: number;
    panelistIds: number;
    screens: number;
    /** Screens by market, biggest first (screen-actions `screenMarkets`). */
    markets: { market: string; screens: number }[];
}

const SheetSchema = z.object({
    name: z.string().max(260),
    rows: z.array(z.array(z.string().max(500)).max(200)).max(100_000),
});

export const SaveListInputSchema = z
    .object({
        kind: z.enum(LIST_KINDS),
        name: z.string().trim().min(2).max(120),
        /** Stores: name, address or lat/lng per row. Placements: lat, lng, radius, type. */
        rows: z.array(z.string().max(500)).max(100_000).optional(),
        /** The file's sheets as read (Files): a workbook's Test and Control sheets keep their groups. */
        sheets: z.array(SheetSchema).max(20).optional(),
        fileName: z.string().max(260).optional(),
        orgId: z.string().trim().max(40).optional(),
        orgName: z.string().trim().max(200).optional(),
        network: z.string().trim().max(80).optional(),
        campaign: z
            .object({
                name: z.string().trim().min(1).max(120),
                flightStart: z
                    .string()
                    .regex(/^\d{4}-\d{2}-\d{2}$/)
                    .optional(),
                flightEnd: z
                    .string()
                    .regex(/^\d{4}-\d{2}-\d{2}$/)
                    .optional(),
            })
            .optional(),
        /** A new version of this saved list. */
        replacesId: z.string().regex(/^\d+$/).optional(),
        mode: z.enum(['replace', 'add']).optional(),
    })
    .refine((v) => (v.rows?.length ?? 0) > 0 || (v.sheets?.length ?? 0) > 0, {
        message: 'The file has no rows.',
    })
    // A new version keeps the client, network and campaign of the one it replaces.
    .refine((v) => v.kind !== 'screens' || v.replacesId || (v.orgId && v.network), {
        message: 'Screens need the client and the network.',
    })
    .refine((v) => v.kind !== 'campaign' || v.replacesId || v.campaign, {
        message: 'A campaign needs its name.',
    })
    .refine((v) => !v.replacesId || v.mode, { message: 'Choose Replace or Add.' });
export type SaveListInput = z.infer<typeof SaveListInputSchema>;

/** A day's ad log for a campaign: a headerless CSV named YYYYMMDD.csv. */
export const SaveAdLogInputSchema = z.object({
    fileName: z.string().min(1).max(260),
    rows: z
        .array(z.array(z.string().max(200)).max(20))
        .min(1)
        .max(500_000),
});
export type SaveAdLogInput = z.infer<typeof SaveAdLogInputSchema>;

export interface AdLogDay {
    runDate: string;
    fileName: string;
    rows: number;
    refused: number;
    createdBy: string;
    createdAt: string;
}

/** A campaign's ad logs: the days that have one, and the flight's days that do not. */
export interface AdLogCoverage {
    days: AdLogDay[];
    missing: string[];
}

/** iOOH zip lookup (A99): the zip codes a list of placement coordinates fall in. */
export const ZipLookupInputSchema = z
    .object({
        listId: z.string().optional(),
        coordinates: z
            .array(
                z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) }),
            )
            .max(5000)
            .optional(),
    })
    .refine((z) => z.listId || z.coordinates?.length, {
        message: 'Pick a list or paste coordinates.',
    });

export interface ZipLookupResponse {
    zips: { zip: string; city: string; state: string; placements: number }[];
    unmatched: number;
}
